import { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useNavigate } from "react-router-dom";
import useApi from "../hooks/useApi";

// Clave de descarte en localStorage: guarda la firma del aviso cerrado, así
// el banner vuelve a aparecer si el admin envía un feedback distinto.
const DISMISS_KEY = "radar_verif_feedback_dismiss";

// Banner del estado de verificación del difusor. Se muestra en las páginas
// donde el difusor aterriza (envíos y perfil) para que el feedback del equipo
// no pase desapercibido. Se autoconsulta, así que se puede soltar en cualquier
// página del difusor sin props.
export default function FeedbackVerificacion({ showPerfilButton = true }) {
  const { loadApi } = useApi();
  const navigate = useNavigate();
  const [verificacion, setVerificacion] = useState(null);
  const [cerrado, setCerrado] = useState(false);

  useEffect(() => {
    loadApi("productor/verificacion", true, "get")
      .then((res) => setVerificacion(res.data))
      .catch(() => {});
  }, []);

  // Verificado o sin datos: no se muestra nada
  if (!verificacion || verificacion.estado !== "pendiente") return null;

  const tieneFeedback = Boolean(verificacion.feedback);
  // firma del aviso actual (para recordar el descarte por feedback concreto)
  const firma = tieneFeedback ? verificacion.feedback : "en_revision";

  // Si el difusor ya cerró este mismo aviso, no volver a mostrarlo
  if (cerrado || localStorage.getItem(DISMISS_KEY) === firma) return null;

  const handleCerrar = () => {
    localStorage.setItem(DISMISS_KEY, firma);
    setCerrado(true);
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 2 }}>
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          borderRadius: "14px",
          borderLeft: `6px solid ${tieneFeedback ? "#F27224" : "#f5b301"}`,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} p={2.5} alignItems="flex-start">
          <IconButton
            size="small"
            onClick={handleCerrar}
            aria-label="Cerrar aviso"
            sx={{ position: "absolute", top: 6, right: 6, color: "#9e9e9e" }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              flexShrink: 0,
              width: 46,
              height: 46,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: tieneFeedback ? "rgba(242,114,36,0.12)" : "rgba(245,179,1,0.15)",
              color: tieneFeedback ? "#F27224" : "#c99700",
            }}
          >
            {tieneFeedback ? (
              <CampaignRoundedIcon />
            ) : (
              <HourglassTopRoundedIcon />
            )}
          </Box>
          <Stack spacing={1} flexGrow={1}>
            {tieneFeedback ? (
              <>
                <Typography variant="h6" fontWeight={800} color="#F27224">
                  ¡Tienes un nuevo feedback del equipo!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revisamos tu medio y te dejamos estos comentarios para
                  completar la verificación:
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#fff7f2",
                    border: "1px solid #ffd9bf",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", color: "#3b3b3b" }}
                  >
                    {verificacion.feedback}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Actualiza tu perfil con esta información y el equipo volverá a
                  revisarlo. Hasta que sea verificado no aparecerás en el
                  listado de difusores.
                </Typography>
                {showPerfilButton && (
                  <Stack direction="row" pt={0.5}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "#F27224",
                        "&:hover": { bgcolor: "#d9611a" },
                        textTransform: "none",
                        borderRadius: "8px",
                      }}
                      onClick={() => navigate("/productor/perfil/edit_profile")}
                    >
                      Actualizar mi perfil
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight={800} color="#c99700">
                  Tu medio está en revisión
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  El equipo de RADARFREIM está revisando tu medio. Hasta que sea
                  verificado no aparecerás en el listado de difusores. Te
                  avisaremos por este medio en cuanto haya novedades.
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
